import React, { useState, useEffect, useRef } from "react";
import "../styles/Modal.css";
import { AudioDevice } from "../types";

const MicrophoneSettingsModal = ({
  isOpen,
  onClose,
  selectedMicrophoneId,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedMicrophoneId: string;
  onSave: (microphoneId: string) => void;
}) => {
  const [audioDevices, setAudioDevices] = useState<AudioDevice[]>([]);
  const [currentMicrophoneId, setCurrentMicrophoneId] =
    useState<string>(selectedMicrophoneId);
  const [isMicTesting, setIsMicTesting] = useState(false);
  const [micLevel, setMicLevel] = useState(0);

  const audioContextRef = useRef<AudioContext | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // 모달 스크롤 방지 효과
  useEffect(() => {
    if (isOpen) {
      // 스크롤 위치 저장
      const scrollY = window.scrollY;

      // body에 modal-open 클래스 추가
      document.body.classList.add("modal-open");

      // 스크롤 위치 설정
      document.body.style.top = `-${scrollY}px`;

      // 정리 함수
      return () => {
        document.body.classList.remove("modal-open");
        document.body.style.top = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // 사용 가능한 오디오 장치 가져오기
  const getAudioDevices = async () => {
    try {
      // 미디어 장치 접근 권한 요청
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // 사용 가능한 모든 미디어 장치 가져오기
      const devices = await navigator.mediaDevices.enumerateDevices();

      // 오디오 입력 장치만 필터링
      const audioInputs = devices
        .filter((device) => device.kind === "audioinput")
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `마이크 ${device.deviceId.slice(0, 5)}...`,
        }));

      setAudioDevices(audioInputs);

      // 기본 마이크 선택
      if (audioInputs.length > 0 && !currentMicrophoneId) {
        setCurrentMicrophoneId(audioInputs[0].deviceId);
      }
    } catch (err) {
      console.error("Error accessing audio devices:", err);
      alert(
        "마이크 접근에 실패했습니다. 브라우저 설정에서 마이크 권한을 허용해주세요."
      );
    }
  };

  // 마이크 테스트 시작
  const startMicTest = async () => {
    // 이미 테스트 중이면 먼저 중지
    if (isMicTesting) {
      stopMicTest();
    }

    // 플래그 변수 (함수 내부에서만 사용)
    let isTestActive = true;

    try {
      // 상태 업데이트
      setIsMicTesting(true);

      // 새 오디오 컨텍스트 생성
      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();

      // 선택한 마이크로 스트림 가져오기
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: currentMicrophoneId
            ? { exact: currentMicrophoneId }
            : undefined,
        },
      });

      // 함수가 여전히 활성 상태인지 확인 (함수 내부 플래그 사용)
      if (!isTestActive) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      audioStreamRef.current = stream;

      // 분석기 설정
      const analyser = audioContextRef.current.createAnalyser();
      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // 오디오 소스 연결
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyser);

      analyserRef.current = analyser;

      // 마이크 레벨 업데이트 함수 정의
      const updateMicLevel = () => {
        // 내부 플래그와 상태 모두 확인
        if (!isTestActive || !analyserRef.current) {
          setMicLevel(0);
          return;
        }

        analyserRef.current.getByteFrequencyData(dataArray);

        // 평균 볼륨 계산
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const average = sum / bufferLength;

        // 0-100 범위로 정규화
        const level = Math.min(100, Math.max(0, (average * 100) / 256));
        setMicLevel(level);

        // 다음 프레임 요청
        animationFrameRef.current = requestAnimationFrame(updateMicLevel);
      };

      // 마이크 레벨 측정 즉시 시작
      animationFrameRef.current = requestAnimationFrame(updateMicLevel);
    } catch (error) {
      alert("마이크 테스트를 시작할 수 없습니다. 마이크 권한을 확인해주세요.");
      isTestActive = false;
      setIsMicTesting(false);
      setMicLevel(0);
    }

    // 함수 내 클린업 함수 정의
    return () => {
      isTestActive = false;
    };
  };

  // 마이크 테스트 중지
  const stopMicTest = () => {
    // 이미 테스트 중이 아니면 무시
    if (
      !isMicTesting &&
      !audioStreamRef.current &&
      !animationFrameRef.current
    ) {
      return;
    }

    // 상태 먼저 업데이트 (UI 빠르게 반응)
    setIsMicTesting(false);

    // 애니메이션 프레임 취소
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // 오디오 스트림 정리
    if (audioStreamRef.current) {
      const tracks = audioStreamRef.current.getTracks();
      if (tracks.length > 0) {
        tracks.forEach((track) => {
          track.stop();
        });
      }
      audioStreamRef.current = null;
    }

    // 오디오 컨텍스트 정리
    if (audioContextRef.current) {
      // 상태 확인 먼저
      if (audioContextRef.current.state !== "closed") {
        audioContextRef.current
          .close()
          .then(() => {
            audioContextRef.current = null;
          })
          .catch((err) => {
            // 오류는 남겨둠
            console.error("오디오 컨텍스트 닫기 오류:", err);
          });
      } else {
        audioContextRef.current = null;
      }
    }

    // 분석기 참조 정리
    analyserRef.current = null;

    // 마이크 레벨 리셋
    setMicLevel(0);
  };

  // 컴포넌트 마운트 시 오디오 장치 가져오기
  useEffect(() => {
    if (isOpen) {
      getAudioDevices();
    }
    return () => {
      stopMicTest();
    };
  }, [isOpen]);

  // 선택된 마이크 변경 처리
  useEffect(() => {
    setCurrentMicrophoneId(selectedMicrophoneId);
  }, [selectedMicrophoneId]);

  // 마이크 선택 변경 핸들러
  const handleMicrophoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMicId = e.target.value;

    // 이전 마이크와 같으면 무시
    if (newMicId === currentMicrophoneId) {
      return;
    }

    // 상태 업데이트
    setCurrentMicrophoneId(newMicId);

    // 마이크 테스트 중이라면 중지 후 재시작
    if (isMicTesting) {
      // 테스트 중지
      stopMicTest();

      // 상태 변화가 반영될 시간을 주기 위해 약간의 지연 후 테스트 재시작
      setTimeout(() => {
        startMicTest();
      }, 300);
    }
  };

  // 모달이 닫힐 때 마이크 테스트 중지
  useEffect(() => {
    if (!isOpen && isMicTesting) {
      stopMicTest();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(currentMicrophoneId);
    stopMicTest();
    onClose();
  };

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        stopMicTest();
        onClose();
      }}
    >
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>마이크 설정</h3>
          <button
            className="modal-close-btn"
            onClick={() => {
              stopMicTest();
              onClose();
            }}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="settings-content">
            <div className="setting-group">
              <h4>마이크 선택</h4>
              <div className="setting-item">
                <select
                  value={currentMicrophoneId}
                  onChange={handleMicrophoneChange}
                  className="microphone-select"
                >
                  <option value="">기본 마이크</option>
                  {audioDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="setting-group">
              <h4>마이크 테스트</h4>
              <div className="setting-item">
                <div className="mic-test-container">
                  <button
                    className={`mic-test-btn ${isMicTesting ? "testing" : ""}`}
                    onClick={isMicTesting ? stopMicTest : startMicTest}
                  >
                    {isMicTesting ? "테스트 중지" : "마이크 테스트 시작"}
                  </button>

                  {isMicTesting && (
                    <div className="mic-level-container">
                      <span className="mic-level-label">마이크 레벨:</span>
                      <div className="mic-level-bar">
                        <div
                          className="mic-level-fill"
                          style={{ width: `${micLevel}%` }}
                        />
                      </div>
                      <span className="mic-level-value">
                        {Math.round(micLevel)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn-cancel"
            onClick={() => {
              stopMicTest();
              onClose();
            }}
          >
            취소
          </button>
          <button className="btn-primary" onClick={handleSave}>
            저장
          </button>
        </div>
      </div>
    </div>
  );
};

export default MicrophoneSettingsModal;
